import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useAccount } from './AccountContext';
import { db } from '../lib/firebase';
import { doc, onSnapshot, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { capture } from '../lib/posthog';

export type StepState = 'done' | 'loading' | 'pending';

export interface ChecklistState {
  onboarding: StepState;
  personnalisation: StepState;
  synchronisation: StepState;
  dashboard: StepState;
  premiereAnalyse: StepState;
}

// Route to navigate to when clicking a step
export const STEP_ROUTES: Record<keyof ChecklistState, string> = {
  onboarding: '/demarrage',
  personnalisation: '/dashboard?demo=true&onboardingStep=personnalisation',
  synchronisation: '/demarrage',
  dashboard: '/dashboard',
  premiereAnalyse: '/ia-analysis',
};

interface ActivationChecklistContextType {
  checklistState: ChecklistState;
  isChecklistVisible: boolean;
  isChecklistCompleted: boolean;
  areFirstThreeStepsDone: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
  markPersonnalisationDone: () => void;
  markSynchronisationDone: () => void;
  markDashboardVisited: () => void;
  markPremiereAnalyseDone: () => void;
  completeChecklist: () => void;
  initChecklist: () => void;
}

declare global {
  var __activationChecklistContext__: React.Context<ActivationChecklistContextType | null> | undefined;
}

const ActivationChecklistContext = globalThis.__activationChecklistContext__
  ?? createContext<ActivationChecklistContextType | null>(null);

globalThis.__activationChecklistContext__ = ActivationChecklistContext;

const STORAGE_KEY = (uid: string) => `activationChecklist_${uid}`;
const COMPLETED_KEY = (uid: string) => `activationChecklist_completed_${uid}`;
const checklistDocRef = (uid: string) => doc(db, 'users', uid, 'checklist', 'main');
const userDocRef = (uid: string) => doc(db, 'users', uid);

function readLocalChecklistState(uid: string): ChecklistState {
  const localRaw = localStorage.getItem(STORAGE_KEY(uid));
  if (!localRaw) return INITIAL_STATE;

  try {
    return sanitizeChecklistState(JSON.parse(localRaw) as Partial<ChecklistState>);
  } catch {
    return INITIAL_STATE;
  }
}

async function pauseChecklistForExpiredTrial(uid: string, completedState: ChecklistState) {
  const userSnap = await getDoc(userDocRef(uid));
  if (!userSnap.exists()) return;

  const userData = userSnap.data() as {
    checklistPausedDueToExpiry?: boolean;
    checklistPausedSnapshot?: Partial<ChecklistState>;
  };

  if (userData.checklistPausedDueToExpiry === true) {
    syncChecklistCache(uid, completedState, true);
    return;
  }

  const checklistSnap = await getDoc(checklistDocRef(uid));
  const stateToSnapshot = checklistSnap.exists()
    ? sanitizeChecklistState(checklistSnap.data() as Partial<ChecklistState>)
    : readLocalChecklistState(uid);

  if (allDone(stateToSnapshot)) {
    syncChecklistCache(uid, completedState, true);
    return;
  }

  await updateDoc(userDocRef(uid), {
    checklistPausedDueToExpiry: true,
    checklistPausedSnapshot: stateToSnapshot,
  });

  syncChecklistCache(uid, completedState, true);
}

async function restorePausedChecklist(uid: string): Promise<ChecklistState | null> {
  const userSnap = await getDoc(userDocRef(uid));
  if (!userSnap.exists()) return null;

  const userData = userSnap.data() as {
    checklistPausedDueToExpiry?: boolean;
    checklistPausedSnapshot?: Partial<ChecklistState>;
  };

  if (!userData.checklistPausedDueToExpiry || !userData.checklistPausedSnapshot) {
    return null;
  }

  const restoredState = sanitizeChecklistState(userData.checklistPausedSnapshot);

  await updateDoc(userDocRef(uid), {
    checklistPausedDueToExpiry: false,
    checklistPausedSnapshot: deleteField(),
  });

  return restoredState;
}

const INITIAL_STATE: ChecklistState = {
  onboarding: 'done',
  personnalisation: 'pending',
  synchronisation: 'pending',
  dashboard: 'pending',
  premiereAnalyse: 'pending',
};

const FALLBACK_CONTEXT: ActivationChecklistContextType = {
  checklistState: INITIAL_STATE,
  isChecklistVisible: false,
  isChecklistCompleted: false,
  areFirstThreeStepsDone: false,
  isCollapsed: false,
  setIsCollapsed: () => {},
  markPersonnalisationDone: () => {},
  markSynchronisationDone: () => {},
  markDashboardVisited: () => {},
  markPremiereAnalyseDone: () => {},
  completeChecklist: () => {},
  initChecklist: () => {},
};

const ALL_STEPS: (keyof ChecklistState)[] = ['onboarding', 'personnalisation', 'synchronisation', 'dashboard', 'premiereAnalyse'];
const FIRST_THREE_STEPS: (keyof ChecklistState)[] = ['onboarding', 'synchronisation', 'personnalisation'];

const STEP_LABELS: Record<keyof ChecklistState, string> = {
  onboarding: 'Onboarding',
  synchronisation: 'Synchronisation',
  personnalisation: 'Personnalisation',
  dashboard: 'Découvre ton Dashboard',
  premiereAnalyse: 'Ta première analyse IA',
};

function sanitizeChecklistState(raw?: Partial<Record<keyof ChecklistState, unknown>> | null): ChecklistState {
  const next = { ...INITIAL_STATE };

  for (const key of ALL_STEPS) {
    const value = raw?.[key];
    if (value === 'done' || value === 'loading' || value === 'pending') {
      next[key] = value;
    }
  }

  return next;
}

function syncChecklistCache(uid: string, state: ChecklistState, completed: boolean) {
  localStorage.setItem(STORAGE_KEY(uid), JSON.stringify(state));

  if (completed) {
    localStorage.setItem(COMPLETED_KEY(uid), 'true');
  } else {
    localStorage.removeItem(COMPLETED_KEY(uid));
  }
}

type ChecklistAnalyticsEventKey = 'completed' | `step_${keyof ChecklistState}`;
const POSTHOG_EVENTS_KEY = (uid: string) => `activationChecklist_posthog_events_${uid}`;
const POSTHOG_EVENTS_FIELD = '_posthogEvents';

function stepAnalyticsEventKey(step: keyof ChecklistState): ChecklistAnalyticsEventKey {
  return `step_${step}` as ChecklistAnalyticsEventKey;
}

function readChecklistAnalyticsEvents(uid: string): Set<ChecklistAnalyticsEventKey> {
  const raw = localStorage.getItem(POSTHOG_EVENTS_KEY(uid));
  if (!raw) return new Set();

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is ChecklistAnalyticsEventKey => typeof entry === 'string'));
  } catch {
    return new Set();
  }
}

function persistChecklistAnalyticsEvents(uid: string, events: Set<ChecklistAnalyticsEventKey>) {
  localStorage.setItem(POSTHOG_EVENTS_KEY(uid), JSON.stringify([...events]));

  const eventMap = [...events].reduce<Record<string, boolean>>((acc, eventKey) => {
    acc[eventKey] = true;
    return acc;
  }, {});

  setDoc(checklistDocRef(uid), { [POSTHOG_EVENTS_FIELD]: eventMap }, { merge: true })
    .catch(err => console.error('[Checklist] Firestore analytics flags write error:', err));
}

function hydrateChecklistAnalyticsEvents(uid: string, remoteEvents?: Record<string, unknown>): Set<ChecklistAnalyticsEventKey> {
  const events = readChecklistAnalyticsEvents(uid);

  for (const [key, value] of Object.entries(remoteEvents ?? {})) {
    if (value === true && (key === 'completed' || key.startsWith('step_'))) {
      events.add(key as ChecklistAnalyticsEventKey);
    }
  }

  localStorage.setItem(POSTHOG_EVENTS_KEY(uid), JSON.stringify([...events]));
  return events;
}

export function isBrokerSyncedAccount(account: Record<string, unknown>): boolean {
  return account.platform === 'ctrader'
    || account.platform === 'tradelocker'
    || account.platform === 'tradovate'
    || account.platform === 'mt4'
    || account.platform === 'mt5'
    || account.metaTrader !== undefined
    || account.type === 'newMetatrader';
}

/** Même logique que AccountManager / Journal : sync initiale tant que accountVerified est absent. */
export function isBrokerAccountFullySynced(account: Record<string, unknown>): boolean {
  if (!isBrokerSyncedAccount(account)) {
    return false;
  }

  if (account.accountVerified === 'problem') {
    return false;
  }

  if (account.accountVerified === null || account.accountVerified === undefined) {
    return false;
  }

  return account.accountVerified === true || account.accountVerified === 'verified';
}

export function isAccountSynchronizationDone(account: Record<string, unknown>): boolean {
  if (!isBrokerSyncedAccount(account)) {
    return true;
  }

  return isBrokerAccountFullySynced(account);
}

function allDone(state: ChecklistState): boolean {
  return ALL_STEPS.every(k => state[k] === 'done');
}

function areFirstStepsDone(state: ChecklistState): boolean {
  return FIRST_THREE_STEPS.every(k => state[k] === 'done');
}

export function ActivationChecklistProvider({ children }: { children: React.ReactNode }) {
  const { user, isPremium, isTrialExpired, hasActiveTrialOnboarding, loading } = useAuth();
  const { accounts } = useAccount();
  const [checklistState, setChecklistState] = useState<ChecklistState>(INITIAL_STATE);
  const [isChecklistVisible, setIsChecklistVisible] = useState(false);
  const [isChecklistCompleted, setIsChecklistCompleted] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [checklistKey, setChecklistKey] = useState(0);
  const analyticsEventsSentRef = useRef<Set<ChecklistAnalyticsEventKey>>(new Set());

  const completedState: ChecklistState = {
    onboarding: 'done',
    personnalisation: 'done',
    synchronisation: 'done',
    dashboard: 'done',
    premiereAnalyse: 'done',
  };
  // Source unique : AuthContext (essai actif, ni expiré ni converti en abonnement payant)
  const hasTrialOnboarding = hasActiveTrialOnboarding;

  const persistToFirestore = useCallback((uid: string, state: ChecklistState, completed: boolean) => {
    setDoc(checklistDocRef(uid), { ...state, _completed: completed }, { merge: true })
      .catch(err => console.error('[Checklist] Firestore write error:', err));
  }, []);

  const updateStep = useCallback((step: keyof ChecklistState, state: StepState) => {
    if (!user?.uid) return;
    const uid = user.uid;

    setChecklistState(prev => {
      if (prev[step] === state) return prev;

      const next = { ...prev, [step]: state };
      const completed = allDone(next);

      syncChecklistCache(uid, next, completed);

      if (analyticsEventsSentRef.current.size === 0) {
        analyticsEventsSentRef.current = hydrateChecklistAnalyticsEvents(uid);
      }
      const analyticsEventsSent = analyticsEventsSentRef.current;

      const emitOnce = (eventKey: ChecklistAnalyticsEventKey, emit: () => void) => {
        if (analyticsEventsSent.has(eventKey)) return;
        analyticsEventsSent.add(eventKey);
        persistChecklistAnalyticsEvents(uid, analyticsEventsSent);
        emit();
      };

      if (state === 'done' && prev[step] !== 'done') {
        emitOnce(stepAnalyticsEventKey(step), () => {
          capture('activation_checklist_step_completed', {
            step,
            step_label: STEP_LABELS[step],
          });
        });
      }

      if (completed) {
        emitOnce('completed', () => {
          capture('activation_checklist_completed', {
            steps_completed: ALL_STEPS.length,
          });
        });
        setIsChecklistCompleted(true);
        persistToFirestore(uid, next, true);
        setIsChecklistVisible(false);
      } else if (state === 'done') {
        setIsChecklistCompleted(false);
        persistToFirestore(uid, next, false);
      } else {
        setIsChecklistCompleted(false);
      }

      return next;
    });
  }, [user?.uid, persistToFirestore]);

  useEffect(() => {
    if (loading) return;

    if (!user?.uid) {
      analyticsEventsSentRef.current = new Set();
      setChecklistState(INITIAL_STATE);
      setIsChecklistVisible(false);
      setIsChecklistCompleted(false);
      return;
    }

    const uid = user.uid;
    analyticsEventsSentRef.current = hydrateChecklistAnalyticsEvents(uid);

    // Essai expiré : checklist terminée côté UI + snapshot sauvegardé sur le doc user.
    if (isTrialExpired) {
      setChecklistState(completedState);
      setIsChecklistCompleted(true);
      setIsChecklistVisible(false);
      syncChecklistCache(uid, completedState, true);

      pauseChecklistForExpiredTrial(uid, completedState)
        .catch(err => console.error('[Checklist] Erreur pause essai expiré:', err));

      return;
    }

    // Utilisateur redevenu premium : restaurer la progression pausée depuis le doc user.
    if (!hasTrialOnboarding && isPremium) {
      restorePausedChecklist(uid).then(restoredState => {
        if (restoredState) {
          const restoredCompleted = allDone(restoredState);
          syncChecklistCache(uid, restoredState, restoredCompleted);
          setChecklistState(restoredState);
          setIsChecklistCompleted(restoredCompleted);
          setIsChecklistVisible(!restoredCompleted);
          persistToFirestore(uid, restoredState, restoredCompleted);
          return;
        }

        setChecklistState(completedState);
        setIsChecklistVisible(false);
        setIsChecklistCompleted(true);
      }).catch(() => {
        setChecklistState(completedState);
        setIsChecklistVisible(false);
        setIsChecklistCompleted(true);
      });
      return;
    }

    if (!hasTrialOnboarding) {
      setChecklistState(completedState);
      setIsChecklistVisible(false);
      setIsChecklistCompleted(true);
      return;
    }

    const localRaw = localStorage.getItem(STORAGE_KEY(uid));
    const localCompletedFlag = localStorage.getItem(COMPLETED_KEY(uid)) === 'true';

    let localState = INITIAL_STATE;
    try {
      if (localRaw) {
        localState = sanitizeChecklistState(JSON.parse(localRaw) as Partial<ChecklistState>);
      }
    } catch {
      localState = INITIAL_STATE;
    }

    const localCompleted = localCompletedFlag || allDone(localState);

    setChecklistState(localState);
    setIsChecklistCompleted(localCompleted);
    setIsChecklistVisible(!localCompleted);

    getDoc(checklistDocRef(uid)).then(snap => {
      if (snap.exists()) {
        const remote = snap.data() as Partial<ChecklistState> & {
          _completed?: boolean;
          _pausedDueToExpiry?: boolean;
          _posthogEvents?: Record<string, unknown>;
        };
        const remoteState = sanitizeChecklistState(remote);
        const remoteCompleted = remote._completed === true || allDone(remoteState);

        analyticsEventsSentRef.current = hydrateChecklistAnalyticsEvents(uid, remote[POSTHOG_EVENTS_FIELD]);
        syncChecklistCache(uid, remoteState, remoteCompleted);
        setChecklistState(remoteState);
        setIsChecklistCompleted(remoteCompleted);
        setIsChecklistVisible(!remoteCompleted);
        return;
      }

      syncChecklistCache(uid, localState, localCompleted);
      persistToFirestore(uid, localState, localCompleted);
      setChecklistState(localState);
      setIsChecklistCompleted(localCompleted);
      setIsChecklistVisible(!localCompleted);
    }).catch(() => {
      syncChecklistCache(uid, localState, localCompleted);
      setChecklistState(localState);
      setIsChecklistCompleted(localCompleted);
      setIsChecklistVisible(!localCompleted);
    });
  }, [user?.uid, loading, hasTrialOnboarding, isTrialExpired, isPremium, checklistKey, persistToFirestore]);

  useEffect(() => {
    if (!user?.uid || !hasTrialOnboarding || isTrialExpired) return;
    const colorTheme = localStorage.getItem(`colorTheme_${user.uid}`);
    if (colorTheme) {
      updateStep('personnalisation', 'done');
    }
  }, [user?.uid, hasTrialOnboarding, isTrialExpired, updateStep]);

  useEffect(() => {
    if (!user?.uid || !hasTrialOnboarding || isChecklistCompleted || isTrialExpired) return;
    const uid = user.uid;
    const saved = localStorage.getItem(STORAGE_KEY(uid));
    if (!saved) return;

    if (localStorage.getItem(COMPLETED_KEY(uid)) === 'true') {
      return;
    }

    // Cliquet à sens unique : une fois "done", on ne redescend jamais.
    // `accounts` change de référence à chaque tick Firestore (onSnapshot), ce qui provoquait
    // un scintillement done -> loading -> done et un spam de l'event PostHog associé.
    if (checklistState.synchronisation === 'done') return;

    const hasSyncedAccount = accounts.some((account) =>
      isAccountSynchronizationDone(account as Record<string, unknown>),
    );

    const nextSyncState: StepState = hasSyncedAccount
      ? 'done'
      : accounts.length > 0
        ? 'loading'
        : 'pending';

    updateStep('synchronisation', nextSyncState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, hasTrialOnboarding, checklistKey, isChecklistCompleted, isTrialExpired, accounts, updateStep, checklistState.synchronisation]);

  useEffect(() => {
    if (!user?.uid || !hasTrialOnboarding || isChecklistCompleted || isTrialExpired) return;

    const uid = user.uid;

    const unsub = onSnapshot(userDocRef(uid), (snap) => {
      if (localStorage.getItem(COMPLETED_KEY(uid)) === 'true') return;

      const iaAnalysesUsed = Number(snap.data()?.iaAnalysesUsed ?? 0);
      if (iaAnalysesUsed < 1) return;

      updateStep('premiereAnalyse', 'done');
    }, (error) => {
      console.error('[Checklist] Firestore iaAnalysesUsed sync error:', error);
    });

    return () => unsub();
  }, [user?.uid, hasTrialOnboarding, isChecklistCompleted, isTrialExpired, updateStep]);

  const initChecklist = useCallback(() => {
    if (!user?.uid) return;

    // Essai expiré : on ne relance pas la checklist
    if (isTrialExpired) {
      setIsChecklistCompleted(true);
      setIsChecklistVisible(false);
      return;
    }

    if (!hasTrialOnboarding) {
      setChecklistState(completedState);
      setIsChecklistCompleted(true);
      setIsChecklistVisible(false);
      return;
    }

    const uid = user.uid;
    const existing = localStorage.getItem(STORAGE_KEY(uid));

    let localState = INITIAL_STATE;
    try {
      if (existing) {
        localState = sanitizeChecklistState(JSON.parse(existing) as Partial<ChecklistState>);
      }
    } catch {
      localState = INITIAL_STATE;
    }

    const completed = localStorage.getItem(COMPLETED_KEY(uid)) === 'true' || allDone(localState);

    syncChecklistCache(uid, localState, completed);
    setChecklistState(localState);
    setIsChecklistCompleted(completed);
    setIsChecklistVisible(!completed);
    setChecklistKey(k => k + 1);
  }, [user?.uid, hasTrialOnboarding, isTrialExpired]);

  const markPersonnalisationDone = useCallback(() => updateStep('personnalisation', 'done'), [updateStep]);
  const markSynchronisationDone = useCallback(() => updateStep('synchronisation', 'done'), [updateStep]);
  const markDashboardVisited = useCallback(() => updateStep('dashboard', 'done'), [updateStep]);
  const markPremiereAnalyseDone = useCallback(() => updateStep('premiereAnalyse', 'done'), [updateStep]);
  const completeChecklist = useCallback(() => {
    if (!user?.uid) return;

    const uid = user.uid;
    const completedState: ChecklistState = {
      onboarding: 'done',
      personnalisation: 'done',
      synchronisation: 'done',
      dashboard: 'done',
      premiereAnalyse: 'done',
    };

    syncChecklistCache(uid, completedState, true);
    setChecklistState(completedState);
    setIsChecklistCompleted(true);
    setIsChecklistVisible(false);
    persistToFirestore(uid, completedState, true);
  }, [persistToFirestore, user?.uid]);

  // Essai expiré → on masque et on marque comme terminé pour toute l'app
  const effectiveCompleted = isTrialExpired ? true : (hasTrialOnboarding ? isChecklistCompleted : true);
  // Stepper UI désactivé — la progression reste trackée (Firestore + PostHog).
  const effectiveVisible = false;
  const effectiveState = isTrialExpired ? completedState : (hasTrialOnboarding ? checklistState : completedState);
  const effectiveFirstThree = isTrialExpired ? true : (hasTrialOnboarding ? areFirstStepsDone(checklistState) : true);

  return (
    <ActivationChecklistContext.Provider value={{
      checklistState: effectiveState,
      isChecklistVisible: effectiveVisible,
      isChecklistCompleted: effectiveCompleted,
      areFirstThreeStepsDone: effectiveFirstThree,
      isCollapsed,
      setIsCollapsed,
      markPersonnalisationDone,
      markSynchronisationDone,
      markDashboardVisited,
      markPremiereAnalyseDone,
      completeChecklist,
      initChecklist,
    }}>
      {children}
    </ActivationChecklistContext.Provider>
  );
}

export function useActivationChecklist() {
  const ctx = useContext(ActivationChecklistContext);
  if (!ctx) {
    console.warn('[ActivationChecklist] Context unavailable, using fallback state.');
    return FALLBACK_CONTEXT;
  }
  return ctx;
}
